import { PointerEvent as PointerEventReact, useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_AUTO_SCROLL_EDGE = 80
const DEFAULT_AUTO_SCROLL_MAX_SPEED = 18

export interface ICalendarActiveSelection {
    startIndex: number;
    endIndex: number;
}

export interface ICalendarSelectedPeriod {
    startsAt: Date;
    durationSlots: number;
}

interface IDragState {
    pointerId: number;
    startIndex: number;
    currentIndex: number;
    clientY: number;
    captureElement: HTMLElement;
}

interface IProps<TSlot> {
    slots: Date[];
    calendarSlotsByIndex: Map<number, TSlot>;
    canSelectSlot: (slot?: TSlot, slotIndex?: number) => boolean;
    onSelectPeriod: (period: ICalendarSelectedPeriod) => void;
    fixedDurationSlots?: number;
    autoScrollEdge?: number;
    autoScrollMaxSpeed?: number;
}

export const useCalendarSlotSelection = <TSlot, >({
    slots,
    calendarSlotsByIndex,
    canSelectSlot,
    onSelectPeriod,
    fixedDurationSlots,
    autoScrollEdge = DEFAULT_AUTO_SCROLL_EDGE,
    autoScrollMaxSpeed = DEFAULT_AUTO_SCROLL_MAX_SPEED,
}: IProps<TSlot>) => {

    const [activeSelection, setActiveSelection] = useState<ICalendarActiveSelection | null>(null)

    const slotsRef = useRef<HTMLUListElement>(null)
    const dragRef = useRef<IDragState | null>(null)
    const autoScrollFrameRef = useRef<number | null>(null)

    const stopAutoScroll = useCallback(() => {
        if(autoScrollFrameRef.current !== null){
            cancelAnimationFrame(autoScrollFrameRef.current)
            autoScrollFrameRef.current = null
        }
    }, [])

    const getSlotIndexByClientY = useCallback((clientY: number): number | null => {
        const firstSlot = slotsRef.current?.querySelector<HTMLElement>('[data-slot-index="0"]')

        if(!firstSlot || !slots.length) return null

        const slotRect = firstSlot.getBoundingClientRect()
        const rawIndex = Math.floor((clientY - slotRect.top) / slotRect.height)

        return Math.max(0, Math.min(slots.length - 1, rawIndex))
    }, [slots.length])

    const updateSelection = useCallback((clientY: number) => {
        const drag = dragRef.current
        if(!drag) return

        drag.clientY = clientY

        if(fixedDurationSlots) return

        const slotIndex = getSlotIndexByClientY(clientY)
        if(slotIndex === null) return

        const direction = slotIndex >= drag.startIndex ? 1 : -1
        let selectableIndex = drag.startIndex

        for(
            let index = drag.startIndex;
            direction > 0 ? index <= slotIndex : index >= slotIndex;
            index += direction
        ){
            if(!canSelectSlot(calendarSlotsByIndex.get(index), index)) break
            selectableIndex = index
        }

        if(selectableIndex === drag.currentIndex) return

        drag.currentIndex = selectableIndex
        setActiveSelection({
            startIndex: Math.min(drag.startIndex, selectableIndex),
            endIndex: Math.max(drag.startIndex, selectableIndex),
        })
    }, [calendarSlotsByIndex, canSelectSlot, fixedDurationSlots, getSlotIndexByClientY])

    const startAutoScroll = useCallback(() => {
        if(autoScrollFrameRef.current !== null) return

        const scroll = () => {
            const drag = dragRef.current

            if(!drag){
                autoScrollFrameRef.current = null
                return
            }

            const distanceToTop = drag.clientY
            const distanceToBottom = window.innerHeight - drag.clientY
            let scrollSpeed = 0

            if(distanceToTop < autoScrollEdge){
                scrollSpeed = -autoScrollMaxSpeed *
                    (1 - Math.max(distanceToTop, 0) / autoScrollEdge)
            }
            else if(distanceToBottom < autoScrollEdge){
                scrollSpeed = autoScrollMaxSpeed *
                    (1 - Math.max(distanceToBottom, 0) / autoScrollEdge)
            }

            if(scrollSpeed){
                window.scrollBy(0, scrollSpeed)
                updateSelection(drag.clientY)
            }

            autoScrollFrameRef.current = requestAnimationFrame(scroll)
        }

        autoScrollFrameRef.current = requestAnimationFrame(scroll)
    }, [autoScrollEdge, autoScrollMaxSpeed, updateSelection])

    const cancelSelection = useCallback(() => {
        const drag = dragRef.current

        if(drag?.captureElement.hasPointerCapture(drag.pointerId)){
            drag.captureElement.releasePointerCapture(drag.pointerId)
        }

        dragRef.current = null
        setActiveSelection(null)
        stopAutoScroll()
    }, [stopAutoScroll])

    const onPointerDown = useCallback((event: PointerEventReact<HTMLLIElement>, slotIndex: number) => {
        const durationSlots = Math.max(1, fixedDurationSlots || 1)
        const endIndex = slotIndex + durationSlots - 1
        const canSelectRange = endIndex < slots.length &&
            canSelectSlot(calendarSlotsByIndex.get(slotIndex), slotIndex)

        if(event.button !== 0 || !canSelectRange){
            return
        }

        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)

        dragRef.current = {
            pointerId: event.pointerId,
            startIndex: slotIndex,
            currentIndex: endIndex,
            clientY: event.clientY,
            captureElement: event.currentTarget,
        }

        setActiveSelection({startIndex: slotIndex, endIndex})
        startAutoScroll()
    }, [calendarSlotsByIndex, canSelectSlot, fixedDurationSlots, slots.length, startAutoScroll])

    const onPointerMove = useCallback((event: PointerEventReact<HTMLUListElement>) => {
        if(dragRef.current?.pointerId !== event.pointerId) return

        event.preventDefault()
        updateSelection(event.clientY)
    }, [updateSelection])

    const onPointerUp = useCallback((event: PointerEventReact<HTMLUListElement>) => {
        const drag = dragRef.current
        if(!drag || drag.pointerId !== event.pointerId) return

        event.preventDefault()

        const startIndex = Math.min(drag.startIndex, drag.currentIndex)
        const endIndex = Math.max(drag.startIndex, drag.currentIndex)

        if(drag.captureElement.hasPointerCapture(drag.pointerId)){
            drag.captureElement.releasePointerCapture(drag.pointerId)
        }

        dragRef.current = null
        setActiveSelection(null)
        stopAutoScroll()
        onSelectPeriod({
            startsAt: slots[startIndex],
            durationSlots: endIndex - startIndex + 1,
        })
    }, [onSelectPeriod, slots, stopAutoScroll])

    useEffect(() => {
        const updateAfterScroll = () => {
            const drag = dragRef.current
            if(drag) updateSelection(drag.clientY)
        }

        document.addEventListener('scroll', updateAfterScroll, true)
        window.addEventListener('blur', cancelSelection)

        return () => {
            document.removeEventListener('scroll', updateAfterScroll, true)
            window.removeEventListener('blur', cancelSelection)
            stopAutoScroll()
        }
    }, [cancelSelection, stopAutoScroll, updateSelection])

    return {
        slotsRef,
        activeSelection,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        cancelSelection,
    }
}
